import { legalServiceSchema } from "@/lib/firm";
import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FIRM, FIRM_ADDRESS, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";
import {
  PROBATE_FEES,
  PROBATE_LOCATIONS,
  getProbateLocation,
  type ProbateLocation,
} from "@/lib/probate-locations";

const SITE = "https://coastsidelaw.com.au";

export const Route = createFileRoute("/probate/$suburb")({
  loader: ({ params }) => {
    const content = getProbateLocation(params.suburb);
    if (!content) throw notFound();
    return { content };
  },
  head: ({ params, loaderData }) => {
    const content = loaderData?.content;
    if (!content) {
      return { meta: [{ title: fitTitle("Not found") }, { name: "robots", content: "noindex" }] };
    }
    const url = `${SITE}/probate/${params.suburb}`;
    return {
      meta: [
        { title: fitTitle(content.metaTitle) },
        { name: "description", content: fitDescription(content.description) },
        { property: "og:title", content: fitTitle(content.metaTitle) },
        { property: "og:description", content: fitDescription(content.description) },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqSchema(content.faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LegalService",
            name: `${FIRM.name} — probate solicitors, ${content.name}`,
            description: content.description,
            telephone: FIRM.phone,
            address: legalServiceSchema.address,
            parentOrganization: { "@id": "https://coastsidelaw.com.au/#legalservice" },
            areaServed: [content.name, ...content.nearby].map((n) => ({
              "@type": "Place",
              name: `${n}, Queensland`,
            })),
            ...(content.geo
              ? {
                  geo: {
                    "@type": "GeoCoordinates",
                    latitude: content.geo.lat,
                    longitude: content.geo.lng,
                  },
                }
              : {}),
            url,
            serviceType: [
              "Grant of probate",
              "Letters of administration",
              "Deceased estate administration",
              "Transmission application",
            ],
            offers: [
              {
                "@type": "Offer",
                name: "Grant of probate",
                price: PROBATE_FEES.grant.replace("$", ""),
                priceCurrency: "AUD",
              },
              {
                "@type": "Offer",
                name: "Full estate administration",
                price: PROBATE_FEES.admin.replace("$", ""),
                priceCurrency: "AUD",
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Probate", item: `${SITE}/probate` },
              { "@type": "ListItem", position: 3, name: content.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: ProbateLocationPage,
});

const GUIDE_LINKS = [
  { slug: "do-i-need-probate-in-queensland", t: "Do I need probate in Queensland?" },
  {
    slug: "what-happens-if-you-die-without-a-will-in-queensland",
    t: "Dying without a will in Queensland",
  },
  {
    slug: "estate-contested-executor-guide-queensland",
    t: "What an executor does when the estate is contested",
  },
] as const;

function ProbateLocationPage() {
  const { content } = Route.useLoaderData() as { content: ProbateLocation };
  const others = PROBATE_LOCATIONS.filter((l) => l.slug !== content.slug);

  return (
    <>
      <PageHero eyebrow={`Probate — ${content.name}`} title={content.title} lead={content.lead} />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Grant of probate
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{PROBATE_FEES.grant}</p>
            <p className="mt-1 text-sm text-muted-foreground">including GST</p>
          </div>
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Full estate administration
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{PROBATE_FEES.admin}</p>
            <p className="mt-1 text-sm text-muted-foreground">including GST</p>
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          The Supreme Court filing fee and the cost of publishing the required notice are set by
          others and charged at cost. No percentage of the estate. See the{" "}
          <Link to="/probate" className="font-semibold text-primary underline">
            full probate page
          </Link>{" "}
          for what each fee covers.
        </p>

        {content.paragraphs.map((p) => (
          <p key={p} className="mt-6 text-base leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}

        {content.localHeading ? (
          <>
            <h2 className="mt-14 font-serif text-3xl text-foreground">{content.localHeading}</h2>
            {(content.localBody ?? []).map((p) => (
              <p key={p} className="mt-5 text-base leading-relaxed text-muted-foreground">
                {p}
              </p>
            ))}
            <dl className="mt-8 grid gap-5 sm:grid-cols-2">
              {(content.localFacts ?? []).map((f) => (
                <div key={f.t} className="rounded-sm border border-border bg-card p-6">
                  <dt className="font-serif text-lg text-foreground">{f.t}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.d}</dd>
                </div>
              ))}
            </dl>
            {content.postcodes?.length ? (
              <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                <span className="font-semibold text-foreground">Areas and postcodes served:</span>{" "}
                {content.postcodes.join(" · ")}
              </p>
            ) : null}
          </>
        ) : null}

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          What comes up on a {content.name} estate
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {content.points.map((c) => (
            <div key={c.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{c.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Where we meet you</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Our Sunshine Coast office is at {FIRM_ADDRESS} and our peninsula office at{" "}
          {PENINSULA_ADDRESS}, both by appointment. We act for executors across {content.name} and{" "}
          {content.nearby.join(", ")}, and most of an estate file runs perfectly well by phone and
          email.
        </p>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} heading="Who will handle it" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={content.faqs} />
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Related pages and guides</h2>
        <ul className="mt-5 space-y-2 text-sm">
          <li>
            <Link to="/wills-and-estates" className="font-semibold text-primary underline">
              Wills, estates and elder law
            </Link>
          </li>
          <li>
            <Link to="/estate-disputes" className="font-semibold text-primary underline">
              Estate disputes and family provision claims
            </Link>
          </li>
          {GUIDE_LINKS.map((g) => (
            <li key={g.slug}>
              <Link
                to="/guides/$slug"
                params={{ slug: g.slug }}
                className="font-semibold text-primary underline"
              >
                {g.t}
              </Link>
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Probate in nearby areas</h2>
        <ul className="mt-5 flex flex-wrap gap-2">
          {others.map((l) => (
            <li key={l.slug}>
              <Link
                to="/probate/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Probate {l.name}
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-14">
          <ExecutorIntakeForm heading={`Start a ${content.name} probate enquiry`} />
        </div>
      </section>

      <CtaBand
        title="Send us the will and we will tell you what is needed"
        body={`Call ${FIRM.phone} or send the will and a list of assets through. We will confirm whether a grant is required and give you the fee in writing.`}
      />
      <div className="h-20" />
    </>
  );
}
